import { EnumPerfiles } from "../../../globales/enums/enumPerfiles";
import { EnumPermisos } from "../../../globales/enums/enumPermisos";
import { EnumTipoTarea } from "../enums/enumTipoTarea";
import type { ITareaFormulario } from "../interfaces/ITareaFormulario";
import type { IProyecto } from "../../../modelos/Proyectos/entidades/IProyecto";
import { EnumTipoContrato } from "../../contratos/enums/enumTipoContrato";

// Visita Instalación solo aplica a proyectos con contrato vigente de Instalación
export const esProyectoInstalacion = (proyecto: IProyecto | undefined) =>
    proyecto?.Contrato?.TipoContratoId === EnumTipoContrato.Instalacion;

// Visita Instalación aplica solo a contratos de Instalación y las tareas SER solo a los demás contratos.
// Sin tipo de tarea seleccionado aplican todos los proyectos.
export const tipoContratoAplicaATipoTarea = (tipoContratoId: number | null | undefined, tipoTareaId: number) => {
    if (!tipoTareaId) return true;
    const esInstalacion = tipoContratoId === EnumTipoContrato.Instalacion;
    return tipoTareaId === EnumTipoTarea.VisitaInstalacion ? esInstalacion : !esInstalacion;
};

export const mensajeContratoRequerido = (tipoTareaId: number) =>
    tipoTareaId === EnumTipoTarea.VisitaInstalacion
        ? 'requiere contrato de Instalación'
        : 'no aplica a contratos de Instalación';

export const proyectoAplicaATipoTarea =(proyecto: IProyecto | undefined, tipoTareaId: number) =>
    tipoContratoAplicaATipoTarea(proyecto?.Contrato?.TipoContratoId, tipoTareaId);

export type IClasificacionTarea = Pick<ITareaFormulario, 'ProyectoId' | 'EquipoId' | 'TipoServicioId' | 'TipoVisitaInstalacionId' | 'AsignadaA'>;

// Indica si la tarea en edición conserva el proyecto, equipo, tipo y actividad con que se abrió
export const conservaClasificacion = (tarea: IClasificacionTarea, original: IClasificacionTarea | null) =>
    !!original &&
    tarea.ProyectoId === original.ProyectoId &&
    tarea.EquipoId === original.EquipoId &&
    tarea.TipoServicioId === original.TipoServicioId &&
    (tarea.TipoVisitaInstalacionId ?? null) === (original.TipoVisitaInstalacionId ?? null);

// Códigos de Verificación del catálogo TipoVisitaInstalacion (espejo de TipoVisitaInstalacionCodigos en el backend).
// La Verificación está partida por modelo de equipo; 'VERIFICACION' es el código genérico anterior, se conserva por histórico.
const CODIGOS_ACTIVIDAD_VERIFICACION = [
    'VERIFICACION_MONOSPACE',
    'VERIFICACION_MINISPACE',
    'VERIFICACION_ESCALERAS',
    'VERIFICACION'
];

export const esActividadVerificacion = (codigoActividad: string) => CODIGOS_ACTIVIDAD_VERIFICACION.includes(codigoActividad);

/**
 * Actividades de Visita Instalación que el usuario ve al consultar tareas (Tareas, su exportación y Agenda). Sin
 * PERMISO_VISITA_INSTALACION_TODAS_ACTIVIDADES (Analista), solo Verificación; con él, undefined: el campo no se envía.
 * El filtro lo aplica el back, antes de paginar.
 */
export const codigosVisitaInstalacionConsulta = (codigosPermiso: string[]): string[] | undefined =>
    codigosPermiso.includes(EnumPermisos.PERMISO_VISITA_INSTALACION_TODAS_ACTIVIDADES) ? undefined : [...CODIGOS_ACTIVIDAD_VERIFICACION];

export interface IPermisosAsignacionTarea {
    crearVisitaInstalacion: boolean;
    todasActividadesNBS: boolean;
    asignarTecnico: boolean;
    asignarTecnicoNBS: boolean;
    asignarVerificadorCalidad: boolean;
}

export const obtenerPermisosAsignacionTarea = (codigosPermiso: string[]): IPermisosAsignacionTarea => {
    const tiene = (permiso: EnumPermisos) => codigosPermiso.includes(permiso);
    return {
        crearVisitaInstalacion: tiene(EnumPermisos.PERMISO_VISITA_INSTALACION_CREAR),
        todasActividadesNBS: tiene(EnumPermisos.PERMISO_VISITA_INSTALACION_TODAS_ACTIVIDADES),
        asignarTecnico: tiene(EnumPermisos.PERMISO_TAREAS_ASIGNAR_TECNICO),
        asignarTecnicoNBS: tiene(EnumPermisos.PERMISO_TAREAS_ASIGNAR_TECNICO_NBS),
        asignarVerificadorCalidad: tiene(EnumPermisos.PERMISO_TAREAS_ASIGNAR_VERIFICADOR_CALIDAD),
    };
};

// Perfiles de técnico que el usuario puede asignar en una Visita Instalación (según quién programa, no la actividad):
// Analista → técnicos SER, Planeador → técnicos NBS, Administrador Calidad → verificadores
const perfilesVisitaInstalacion = (permisos: IPermisosAsignacionTarea): EnumPerfiles[] => [
    ...(permisos.asignarTecnico ? [EnumPerfiles.Tecnico] : []),
    ...(permisos.asignarTecnicoNBS ? [EnumPerfiles.TecnicoNBS] : []),
    ...(permisos.asignarVerificadorCalidad ? [EnumPerfiles.VerificadorCalidad] : []),
];

// Visita Instalación requiere poder crearla y poder asignarla a algún técnico;
// los demás tipos (servicio SER) requieren poder asignar técnicos SER
export const esTipoTareaPermitido = (tipoTareaId: number, permisos: IPermisosAsignacionTarea) =>
    tipoTareaId === EnumTipoTarea.VisitaInstalacion
        ? permisos.crearVisitaInstalacion && perfilesVisitaInstalacion(permisos).length > 0
        : permisos.asignarTecnico;

// Sin el permiso de todas las actividades solo se puede programar Verificación
export const esActividadNBSPermitida = (codigoActividad: string, permisos: IPermisosAsignacionTarea) =>
    permisos.todasActividadesNBS || esActividadVerificacion(codigoActividad);

export const obtenerPerfilesTecnicoPermitidos = (
    tipoTareaId: number,
    codigoActividad: string | undefined,
    permisos: IPermisosAsignacionTarea
): EnumPerfiles[] => {
    if (tipoTareaId !== EnumTipoTarea.VisitaInstalacion) {
        return permisos.asignarTecnico ? [EnumPerfiles.Tecnico] : [];
    }
    // En Visita Instalación el técnico se elige después de la actividad
    if (!codigoActividad || !esActividadNBSPermitida(codigoActividad, permisos)) return [];
    return perfilesVisitaInstalacion(permisos);
};
